import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LetDirective, PushPipe } from '@ngrx/component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  lastValueFrom,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import * as _ from 'lodash';
import {
  ActionType,
  AlertCategory,
  AlertsV2Plates,
  AlertsV2VehicleSelection,
  AlertsV2Settings,
  AlertsV2ShowSettings,
  AlertsV2Vehicles,
  AlertV2Action,
  AlertV2Document,
  AlertV2Flow,
  AlertV2Schedule,
  AlertV2SelectedAction,
  AlertV2SelectedFlow,
  AlertV2Type,
  AutoArchive,
  CallCategory,
  CallData,
  CustomizedCapabilitiesType,
  EventModels,
  EventV2Document,
  HealthMetric,
  HealthMetricDegreeUnit,
  HealthMetricUtilizationUnit,
  IdentificationType,
  IntegrationsType,
  LprUiMode,
  MonitoringType,
  RetailType,
  SafetyType,
  SettingType,
  StateType,
  StatusType,
  StepType,
  SystemType,
  TrackingType,
} from '@models/alerts-v2.model';
import { resolveLprUiMode } from '../../../../../../pages/alerts-v2/utils/lpr-ui-mode.util';
import {
  AlertsV2Actions,
  AlertsV2Flows,
  AlertsV2SettingActions,
  AlertV2CallGroup,
  AlertV2TypeGroup,
  AlertV2TypeSelectionStr,
  getMessagesByFlowType,
  getVariablesByFlowType,
  integrationActions,
} from '@consts/alerts-v2.const';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { IntegrationsModel } from '@models/integrations.model';
import { HttpRequestModel } from '@models/http-request.model';
import { CustomEventModel } from '@models/custom-event.model';
import { AlarmModel } from '@models/alarm.model';
import { MicrosoftTeamsModels } from '@models/microsoft-teams.model';
import { CameraLookup } from '@models/camera.model';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { ZoneRegionsModels } from '@share-models';
import { AlertsV2StoreActions } from '@states/alerts-v2/alerts-v2.action-types';
import { AlertsV2Selectors } from '@states/alerts-v2/alerts-v2.selector-types';
import {
  baseAlertsV2Settings,
  baseAlertsV2Settings2,
  initialAlertSettings,
} from '@states/alerts-v2/alerts-v2.reducer';
import { CameraEditActions } from '@states/camera-edit/camera-edit.action-types';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { CustomEventsSelectors } from '@states/custom-events/custom-events.selector-types';
import { DoorsActions } from '@states/doors/doors.action-types';
import { IntegrationsActions } from '@states/integrations/integrations.action-types';
import { IntegrationsSelectors } from '@states/integrations/integrations.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { OrgVariablesActions } from '@states/org-variables/org-variables.action-types';
import { OrgVariablesSelectors } from '@states/org-variables/org-variables.selector-type';
import { SharedActions } from '@states/shared/shared.action-types';
import { SharedSelectors } from '@states/shared/shared.selector-types';
import { ShelfsSelectors } from '@states/shelfs/shelfs.selector-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { UniformsActions } from '@states/uniforms/uniforms.action-types';
import { AppState } from '../../../../../../store/app.state';
import { AlertsV2Module } from '../../../../../../pages/alerts-v2/alerts-v2.module';
import { StepOptionSelectorModule } from '../../../../../../pages/alerts-v2/components/step-option-selector/step-option-selector.module';
import { StepOptionSelectorGroup } from '../../../../../../pages/alerts-v2/components/step-option-selector/step-option-selector.component';
import { StepHttpRequestPickerComponent } from '../../../../../../pages/alerts-v2/components/http-request-picker/step-http-request-picker.component';
import { StepMicrosoftTeamsPickerComponent } from '../../../../../../pages/alerts-v2/components/step-option-selector/step-microsoft-teams-picker/step-microsoft-teams-picker.component';
import { StepNotificationsComponent } from '../../../../../../pages/alerts-v2/components/step-notifications/step-notifications.component';
import { OrgVariablesPickerComponent } from '../../../../../../pages/alerts-v2/components/org-variables-picker/org-variables-picker.component';
import { AlertGroupsPickerComponent } from '../../../../../../pages/alerts-v2/components/alert-groups-picker/alert-groups-picker.component';
import { EventEnableTogglePickerComponent } from '../../../../../../pages/alerts-v2/components/event-enable-toggle-picker/event-enable-toggle-picker.component';
import { AlertsV2SettingsDialogComponent } from '../../../../../../pages/alerts-v2/dialogs/alerts-v2-settings-dialog/alerts-v2-settings-dialog.component';
import { StepNotificationMsgDialogResponse } from '../../../../../../pages/alerts-v2/components/step-notification-msg/step-notification-msg-dialog/step-notification-msg-dialog.component';
import { ObjectStateItem } from '../../../../../../pages/alerts-v2/components/step-object-states-selector/step-object-states-selector.component';
import {
  OPTION_CONDITIONS,
  OPTION_CONDITIONS_TEAMS,
} from '../../../../../../pages/alerts-v2/pages/alert-v2-create/alert-v2-create.models';
import { UiSchedulePickerComponent } from '../../../../../ui-kit/ui-schedule-picker/ui-schedule-picker.component';
import { SharedService } from '../../../../../../development/shared.service';
import { KeyValuePairs } from '../../../../../../core/interfaces';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { filterAlertV2TypeGroupByFeatureFlags } from '../alert-type-filter.util';

/**
 * In-panel (Cmd+K) alert creation flow — the "sentence builder" from the alerts
 * page rendered as an overlay result. Opened by alert-templates-result with a
 * `CreateAlertDraft` overlay ('create-alert'). Save/Cancel live in the shared
 * context bar; on save success the panel returns to the alert-configurations list.
 *
 * Controller and template migrated (create-only) from
 * pages/alerts-v2/pages/alert-v2-create/alert-v2-create.component.ts — no edit
 * mode, no policy mode, no v2.0 zone-region picker, no route params. Keep fixes
 * to the shared step handlers (update/loadActionFlow/selectFlow) in sync.
 */
@Component({
  selector: 'app-create-alert-result',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    LetDirective,
    PushPipe,
    MatIconModule,
    AlertsV2Module,
    StepOptionSelectorModule,
    UiSchedulePickerComponent,
    AlertGroupsPickerComponent,
    StepNotificationsComponent,
    StepHttpRequestPickerComponent,
    StepMicrosoftTeamsPickerComponent,
    OrgVariablesPickerComponent,
    EventEnableTogglePickerComponent,
  ],
  templateUrl: './create-alert-result.component.html',
  styleUrls: ['./create-alert-result.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAlertResultComponent implements OnInit, OnDestroy {
  public readonly LprUiMode = LprUiMode;
  public lprUiMode = LprUiMode.Vehicles;
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly intelligenceResultsService = inject(
    IntelligenceResultsService,
  );
  private readonly intelligenceToolsService = inject(IntelligenceToolsService);
  private readonly sharedService = inject(SharedService);
  private readonly utilsService = inject(UtilsService);
  private readonly matDialog = inject(MatDialog);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  public readonly AlertV2Type = AlertV2Type;
  public readonly AlertV2TypeSelectionStr = AlertV2TypeSelectionStr;
  public AlertV2TypeGroup: StepOptionSelectorGroup[] = AlertV2TypeGroup;
  public readonly ActionType = ActionType;
  public readonly AlertCategory = AlertCategory;
  public readonly AlertsV2Flows = AlertsV2Flows;
  public readonly AlertsV2Actions = AlertsV2Actions;
  public readonly AlertsV2SettingActions = AlertsV2SettingActions;
  public readonly StepType = StepType;

  public readonly selectVariablesAutoComplete$: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);
  public readonly AlertV2CallGroup$: BehaviorSubject<
    StepOptionSelectorGroup[]
  > = new BehaviorSubject<StepOptionSelectorGroup[]>([]);

  public readonly selectIntegrations$: Observable<
    IntegrationsModel.Providers[]
  > = this.store$.pipe(select(IntegrationsSelectors.selectIntegrations));
  public readonly selectHttpRequests$: Observable<
    HttpRequestModel.DocumentMongo[]
  > = this.store$.pipe(select(AlertsV2Selectors.selectHttpRequests));
  public readonly selectAlertGroups$: Observable<AlarmModel.AlarmDocument[]> =
    this.store$.pipe(select(AlertsV2Selectors.selectAlertGroups));
  public readonly selectAllOrganizationEvents$: Observable<AlertV2Document[]> =
    this.store$.pipe(select(AlertsV2Selectors.selectAllOrganizationEvents));
  public readonly selectSlackChannels$ = this.store$.pipe(
    select(AlertsV2Selectors.selectSlackChannels),
  );
  public readonly selectTeamsChannels$ = this.store$.pipe(
    select(AlertsV2Selectors.selectTeamsChannels),
  );
  public readonly selectOrganizationVariables = this.store$.selectSignal(
    OrgVariablesSelectors.selectVariables,
  );
  public readonly selectSettings: Observable<AlertsV2Settings> =
    this.store$.pipe(select(AlertsV2Selectors.selectSettings));
  public readonly selectHasSpeakers$: Observable<boolean> = this.store$.pipe(
    select(LocationSelectors.selectHasSpeakers),
  );
  public readonly selectIsDeveloper$: Observable<boolean> = this.store$.pipe(
    select(OrganizationSelectors.isDeveloper),
  );
  public readonly selectEnablePalestinianLicensePlateSig = this.store$.selectSignal(
    OrganizationSelectors.selectEnablePalestinianLicensePlate,
  );
  public readonly selectDisableMissingObjectEventConfigurationSig = this.store$.selectSignal(
    OrganizationSelectors.selectDisableMissingObjectEventConfiguration,
  );
  public readonly selectIsDeveloperSig = this.store$.selectSignal(OrganizationSelectors.isDeveloper);
  public readonly selectAllCustomEvents$: Observable<
    CustomEventModel.CustomEvent[]
  > = this.store$.pipe(select(CustomEventsSelectors.selectAllCustomEvents));

  private readonly alertValid = this.store$.selectSignal(
    AlertsV2Selectors.selectValid,
  );
  private readonly isSaving = this.store$.selectSignal(
    SharedSelectors.selectIsSaving,
  );

  /** Overlay payload set by alert-templates-result; null once another result is shown. */
  private readonly draft =
    computed<IntelligenceModels.AlertConfigurations.CreateAlertDraft | null>(
      () => {
        const result = this.intelligenceResultsService.current();
        if (result?.type !== 'create-alert') return null;
        return result.data as IntelligenceModels.AlertConfigurations.CreateAlertDraft;
      },
    );
  private readonly editConfiguration = computed<EventV2Document | null>(
    () => this.draft()?.configuration ?? null,
  );

  public templateCategory: AlertCategory | null = null;
  public templateFlow: number | null = null;
  public isTemplate = false;
  public sentenceStart = 'When';

  public currentStep = 0;

  public settingsForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    type: new UntypedFormControl('', [Validators.required]),
    scheduleId: new UntypedFormControl(''),
  });
  public configurationForm: UntypedFormGroup = new UntypedFormGroup({
    query: new UntypedFormControl(''),
  });
  private configurationFormSub: Subscription | null = null;
  private editBaseFormValue: Record<string, unknown> | null = null;
  private originalActions: AlertV2SelectedAction[] = [];
  private editableActionSlots: number[] = [];

  public selectedFlowCategory: AlertCategory;
  public selectedFlowType:
    | SafetyType
    | IdentificationType
    | TrackingType
    | StatusType
    | CustomizedCapabilitiesType
    | IntegrationsType
    | RetailType
    | StateType
    | MonitoringType
    | SystemType;
  public selectedAlertFlow: AlertV2Flow | null = null;
  public actions: AlertV2Action[] = [];

  public disableOptions: {
    groupIdx: number;
    optionCategory: CallCategory;
    optionType: ActionType | SettingType;
  }[] = [];

  private _settings: {
    priority: string;
    reactivationTh: number;
    autoArchive: AutoArchive;
    show: { disabled: boolean; on: AlertsV2ShowSettings[] };
    confidence: string;
    message: string;
    scheduleId?: string;
  };
  private alertMessage = '';

  public eventTags: CustomEventModel.CustomEvent[] = [];
  public eventTagIds: string[] = [];
  public eventTagNames: string[] = [];
  public eventTagsKeyValuePairs: KeyValuePairs<string> = {};

  public selectedPhysicalDoor: any = null;
  public selectedObjectStateStates: string[] = [];
  public readonly isVersion20 = signal(false);
  public readonly selectedZoneRegionGroups = signal<ZoneRegionsModels.ZoneRegionDocument[]>([]);

  public constructor() {
    effect(() => {
      this.AlertV2TypeGroup = filterAlertV2TypeGroupByFeatureFlags(
        AlertV2TypeGroup,
        {
          enablePalestinianLicensePlate: this.selectEnablePalestinianLicensePlateSig(),
          disableMissingObjectEventConfiguration: this.selectDisableMissingObjectEventConfigurationSig(),
        },
        this.selectIsDeveloperSig(),
      );
    });
    this.intelligenceResultsService.setHeaderActions([
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_DEFAULT,
        label: 'COMMON.CANCEL',
        tooltip: 'COMMON.CANCEL',
        onClick: () => this.cancel(),
        disabled: () => this.isSaving(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_PRIMARY,
        label: this.editConfiguration()
          ? 'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.SAVE_CHANGES'
          : 'COMMON.SAVE',
        tooltip: this.editConfiguration()
          ? 'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.SAVE_CHANGES'
          : 'COMMON.SAVE',
        onClick: () => this.save(),
        disabled: () => !this.alertValid(),
        loading: () => this.isSaving(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.ICON,
        icon: 'ui-icon-new-tab',
        tooltip: this.editConfiguration()
          ? 'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.OPEN_EDIT_PAGE'
          : 'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.OPEN_CREATE_PAGE',
        onClick: () => this.openAlertCreatePage(),
      },
    ]);
  }

  public ngOnInit(): void {
    // Keep this editor's draft isolated without clearing the alert list shown
    // by a concurrently open alerts page.
    this.store$.dispatch(AlertsV2StoreActions.resetToInitialState());

    // Data for the condition/action pickers (same set the create page loads).
    this.store$.dispatch(AlertsV2StoreActions.getHttpRequests());
    this.store$.dispatch(AlertsV2StoreActions.getAlertGroups());
    this.store$.dispatch(AlertsV2StoreActions.getRemoteIOs());
    this.store$.dispatch(AlertsV2StoreActions.getAllOrgEventsNoPaging());
    this.store$.dispatch(AlertsV2StoreActions.getSlackChannels({}));
    this.store$.dispatch(AlertsV2StoreActions.getTeamsChannels());
    this.store$.dispatch(OrgVariablesActions.getOrgVariables());
    this.store$.dispatch(UniformsActions.getSavedUniforms());
    this.store$.dispatch(CameraEditActions.getHasProtectiveGear());
    this.store$.dispatch(CameraEditActions.getHasShoppingCart());
    this.store$.dispatch(DoorsActions.getDoors());
    // The page gets integrations from AlertsV2Resolver, which doesn't run here.
    this.store$.dispatch(IntegrationsActions.getIntegrations());

    this.store$.dispatch(
      AlertsV2StoreActions.setName({
        name: this.settingsForm.get('name').value,
      }),
    );

    this.settingsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        const dirty: any = this.getDirty(this.settingsForm);
        if (dirty.type) {
          this.selectedFlowType = val.type.type;
          this.selectedFlowCategory = val.type.category;
          this.selectFlow();
        }
        if (dirty.name || dirty.name === '') {
          this.store$.dispatch(
            AlertsV2StoreActions.setName({ name: val.name }),
          );
        }
        if (dirty.scheduleId) {
          this.updateSelectedFlowWithScheduleId(val.scheduleId);
        }
        this.settingsForm.markAsPristine();
      });

    this.initSettings();
    void this.initializeDraft();

    this.actions$
      .pipe(
        ofType(AlertsV2StoreActions.saveAlertFromOverlaySuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.returnToConfigurationsList());
  }

  public ngOnDestroy(): void {
    this.intelligenceResultsService.setHeaderActions([]);
    this.store$.dispatch(AlertsV2StoreActions.resetToInitialState());
  }

  // ── Save / Cancel (context-bar actions) ──────────────────────────────────────

  private save(): void {
    if (this.isSaving() || !this.alertValid()) return;
    this.store$.dispatch(AlertsV2StoreActions.saveAlertFromOverlay());
  }

  private cancel(): void {
    this.intelligenceResultsService.clearOverlay();
  }

  /** Open the real creation page in a NEW TAB so the Cmd+K session stays open. */
  private openAlertCreatePage(): void {
    const editId = this.editConfiguration()?._id;
    const segments = editId
      ? ['/', routerSegments.alertsV2, routerSegments.edit, editId]
      : this.templateCategory != null && this.templateFlow != null
        ? [
            '/',
            routerSegments.alertsV2,
            routerSegments.create,
            `${this.templateCategory}`,
            `${this.templateFlow}`,
          ]
        : ['/', routerSegments.alertsV2, routerSegments.templates];
    const tree = this.router.createUrlTree(segments);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin
      ? `${window.location.origin}${path}`
      : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** After a successful save, swap the panel back to a refreshed configurations list. */
  private async returnToConfigurationsList(): Promise<void> {
    try {
      const response =
        await this.intelligenceToolsService.fetchAlertConfigurations({
          limit: IntelligenceModels.AlertConfigurations.DEFAULT_LIMIT,
        });
      this.intelligenceResultsService.replaceCurrentWithAlertConfigurations(
        response,
      );
    } catch (err) {
      this.intelligenceResultsService.clearOverlay();
      this.store$.dispatch(
        SharedActions.showMessage({
          error: this.utilsService.errMessage(err as Error),
        }),
      );
    }
  }

  private async initializeDraft(): Promise<void> {
    const selectedEvent = this.editConfiguration();
    const template = this.draft()?.template ?? null;

    if (!selectedEvent) {
      const draftName = this.draft()?.name ?? '';
      this.settingsForm.patchValue({ name: draftName }, { emitEvent: false });
      this.store$.dispatch(
        AlertsV2StoreActions.setName({ name: draftName }),
      );
      if (template) {
        this.templateCategory = Number(template.category) as AlertCategory;
        this.templateFlow = Number(template.flowType);
        this.selectedFlowCategory = this.templateCategory;
        this.selectedFlowType = this.templateFlow;
        this.isTemplate = true;
        this.sentenceStart = this.flowSelectName(
          this.templateCategory,
          this.templateFlow,
        );
        this.selectFlow();
      }
      await this.initIntegrations();
      this.addAction();
      if (this.isCloudFlow) this.disableCoreActions();
      return;
    }

    const selectedFlow = selectedEvent.selectedFlow;
    this.templateCategory = selectedFlow.category;
    this.templateFlow = selectedFlow.flowType;
    this.selectedFlowCategory = selectedFlow.category;
    this.selectedFlowType =
      selectedFlow.flowType as typeof this.selectedFlowType;
    this.isTemplate = true;
    this.sentenceStart = this.flowSelectName(
      selectedFlow.category,
      selectedFlow.flowType,
    );
    this.editBaseFormValue = _.cloneDeep(selectedFlow.formValue ?? {});
    this.originalActions = _.cloneDeep(selectedEvent.actions ?? []);

    const hasCustomSchedule =
      !selectedFlow.scheduleId &&
      Array.isArray(selectedFlow.formValue?.schedule);
    this.settingsForm.patchValue(
      {
        name: selectedEvent.name,
        type: { type: selectedFlow.flowType, category: selectedFlow.category },
        scheduleId: hasCustomSchedule
          ? '__CUSTOM_SCHEDULE__'
          : (selectedFlow.scheduleId ?? ''),
      },
      { emitEvent: false },
    );
    this.store$.dispatch(
      AlertsV2StoreActions.setName({ name: selectedEvent.name }),
    );

    await this.initIntegrations();
    this.selectFlow(selectedFlow);
    if (selectedEvent.zoneRegions?.length) {
      this.isVersion20.set(true);
      const groups = [
        ...new Map(selectedEvent.zoneRegions.map((zoneRegion) => [zoneRegion._id, zoneRegion])).values(),
      ];
      this.selectedZoneRegionGroups.set(groups);
      const formValue = selectedFlow.formValue as { zoneRegionsIds?: string[] };
      const zoneRegionsIds = formValue.zoneRegionsIds?.length
        ? formValue.zoneRegionsIds
        : groups.map((group) => group._id);
      if (!this.configurationForm.contains('zoneRegionsIds')) {
        this.configurationForm.addControl('zoneRegionsIds', new FormControl(zoneRegionsIds));
      } else {
        this.configurationForm.patchValue({ zoneRegionsIds });
      }
    }
    this.store$.dispatch(
      AlertsV2StoreActions.setSelectedFlow({
        selectedFlow: {
          ..._.cloneDeep(selectedFlow),
          formValue: _.cloneDeep(selectedFlow.formValue),
        },
      }),
    );
    this.hydrateActions(this.originalActions);

    this.store$.dispatch(
      AlertsV2StoreActions.setSelectedEvent({ selectedEvent }),
    );
    this.store$.dispatch(AlertsV2StoreActions.setValid({ valid: true }));
    this.store$.dispatch(
      AlertsV2StoreActions.setSettings({ settings: selectedEvent.settings }),
    );
    this._settings = {
      priority: String(
        selectedEvent.settings?.priority ?? initialAlertSettings.priority,
      ),
      confidence: String(
        selectedEvent.settings?.confidence ?? initialAlertSettings.confidence,
      ),
      reactivationTh:
        selectedEvent.settings?.reactivationTh ??
        initialAlertSettings.reactivationTh,
      autoArchive: _.cloneDeep(
        selectedEvent.settings?.autoArchive ?? initialAlertSettings.autoArchive,
      ),
      show: _.cloneDeep(
        selectedEvent.settings?.show ?? {
          disabled: false,
          on: [AlertsV2ShowSettings.ALERT_PAGE, AlertsV2ShowSettings.WALLS],
        },
      ),
      message: selectedEvent.settings?.message ?? initialAlertSettings.message,
      scheduleId: selectedFlow.scheduleId,
    };
    if (this.isCloudFlow) this.disableCoreActions();
    this.changeDetectorRef.detectChanges();
  }

  private flowSelectName(category: number, flowType: number): string {
    return (
      AlertV2TypeGroup.flatMap((group) => group.options)
        .map((option) => option.value)
        .find(
          (option) => option?.category === category && option.type === flowType,
        )?.selectName ?? 'When'
    );
  }

  // ── Flow (condition steps) — migrated from AlertV2CreateComponent ────────────

  public selectFlow(selectedFlow?: AlertV2SelectedFlow): void {
    if (selectedFlow) {
      this.selectedFlowCategory = selectedFlow.category;
      this.selectedFlowType =
        selectedFlow.flowType as typeof this.selectedFlowType;
    }
    const typeControlValue = this.settingsForm.get('type')?.value;
    const flowCategoryRaw =
      this.selectedFlowCategory ??
      typeControlValue?.category ??
      this.templateCategory;
    const flowTypeRaw =
      this.selectedFlowType ?? typeControlValue?.type ?? this.templateFlow;

    const selectedCategory = Number(flowCategoryRaw);
    const selectedType = Number(flowTypeRaw);

    const baseFlow = this.AlertsV2Flows.find((flow) => {
      return (
        Number(flow.category) === selectedCategory &&
        Number(flow.flowType) === selectedType
      );
    });

    this.selectedAlertFlow = baseFlow ? _.cloneDeep(baseFlow) : null;
    if (!this.selectedAlertFlow) {
      return;
    }

    this.configurationForm = this.selectedAlertFlow.form;
    if (
      this.selectedAlertFlow.category === AlertCategory.Identification &&
      this.selectedAlertFlow.flowType === IdentificationType.LPR
    ) {
      this.lprUiMode = resolveLprUiMode(selectedFlow?.formValue, !selectedFlow);
      if (this.lprUiMode === LprUiMode.LegacyPlates) {
        this.configurationForm.removeControl('vehicles');
        this.configurationForm.removeControl('vehicleGroups');
      } else if (this.lprUiMode === LprUiMode.Vehicles) {
        this.configurationForm.removeControl('plates');
      }
    }
    if (!!this.configurationFormSub) {
      this.configurationFormSub.unsubscribe();
    }
    this.configurationFormSub = this.configurationForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        const selectedFlow: AlertV2SelectedFlow = {
          category: this.selectedAlertFlow.category,
          flowType: this.selectedAlertFlow.flowType,
          scheduleId: this.settingsForm.get('scheduleId')?.value ?? undefined,
          formValue: this.formValueForActiveLprMode(
            this.editBaseFormValue
              ? { ...this.editBaseFormValue, ...val }
              : val,
          ),
          stepsLength: this.selectedAlertFlow.steps.length,
          currentStep: this.currentStep,
        };
        const variables = getVariablesByFlowType(
          this.selectedFlowType,
          this.selectedFlowCategory,
        );
        this.selectVariablesAutoComplete$.next(variables);
        const messageKey = getMessagesByFlowType(
          selectedFlow.flowType,
          selectedFlow.category,
        );
        this.alertMessage = this.translateService.instant(messageKey);
        this.store$.dispatch(
          AlertsV2StoreActions.setSelectedFlow({ selectedFlow }),
        );
      });

    if (selectedFlow) {
      this.currentStep = this.selectedAlertFlow.steps.length;
      const normalizedFormValue = this.normalizePersonGroupFields(
        selectedFlow.formValue,
      );
      this.configurationForm.patchValue(normalizedFormValue);
      for (const step of this.selectedAlertFlow.steps) {
        step.value = normalizedFormValue[step.formControlName];
      }
      if (
        selectedFlow.category === AlertCategory.Monitoring &&
        normalizedFormValue['metric'] != null
      ) {
        this.updateMonitoringMetricDependentSteps(
          normalizedFormValue['metric'] as HealthMetric,
        );
      }
      if (selectedFlow.category === AlertCategory.ObjectStates) {
        this.selectedObjectStateStates =
          (normalizedFormValue['objectState'] as ObjectStateItem | undefined)
            ?.states ?? [];
      }
    } else {
      this.currentStep = this.isTemplate
        ? this.selectedAlertFlow.steps.length
        : (this.selectedAlertFlow.initStep ?? 0);
      this.actions = [];
      if (!this.isTemplate) this.addAction();
    }

    const getEventTags =
      this.selectedAlertFlow?.category === AlertCategory.Integrations &&
      (this.selectedAlertFlow?.flowType === IntegrationsType.EventTag ||
        this.selectedAlertFlow?.flowType ===
          IntegrationsType.EventTagPosValidation);
    if (getEventTags) {
      this.getEventTags();
    }
  }

  private formValueForActiveLprMode<T extends Record<string, unknown>>(formValue: T): T {
    if (
      this.selectedAlertFlow?.category !== AlertCategory.Identification ||
      this.selectedAlertFlow?.flowType !== IdentificationType.LPR
    ) {
      return formValue;
    }

    const sanitized = { ...formValue };
    if (this.lprUiMode === LprUiMode.LegacyPlates) {
      delete sanitized['vehicles'];
      delete sanitized['vehicleGroups'];
    } else if (this.lprUiMode === LprUiMode.Vehicles) {
      delete sanitized['plates'];
    }
    return sanitized as T;
  }

  private normalizePersonGroupFields<T>(formValue: T): T {
    if (!formValue || typeof formValue !== 'object') return formValue;
    const value = _.cloneDeep(formValue) as Record<string, unknown>;
    if ('people' in value && !('personGroups' in value) && 'groups' in value) {
      value['personGroups'] = value['groups'];
    }
    if ('people' in value) delete value['groups'];
    if (Array.isArray(value['objects'])) {
      value['objects'] = value['objects'].map((object) => {
        if (!object || typeof object !== 'object') return object;
        const item = _.cloneDeep(object) as Record<string, unknown>;
        if (
          !('includePersonGroups' in item) &&
          Array.isArray(item['includeGroups'])
        ) {
          item['includePersonGroups'] = item['includeGroups'];
        }
        if (
          !('excludePersonGroups' in item) &&
          Array.isArray(item['excludeGroups'])
        ) {
          item['excludePersonGroups'] = item['excludeGroups'];
        }
        delete item['includeGroups'];
        delete item['excludeGroups'];
        return item;
      });
    }
    return value as T;
  }

  public get isCloudFlow(): boolean {
    return (
      this.selectedAlertFlow?.category === AlertCategory.Integrations &&
      (this.selectedAlertFlow?.flowType === IntegrationsType.EventTag ||
        this.selectedAlertFlow?.flowType ===
          IntegrationsType.EventTagPosValidation)
    );
  }

  public incrementStep(): void {
    this.currentStep++;
  }

  public onZoneRegionChange(
    idx: number,
    groups: ZoneRegionsModels.ZoneRegionDocument[],
  ): void {
    this.selectedZoneRegionGroups.set(groups ?? []);
    const zoneRegionsIds = [
      ...new Set((groups ?? []).map((group) => group._id).filter(Boolean)),
    ];
    if (!this.configurationForm.contains('zoneRegionsIds')) {
      this.configurationForm.addControl('zoneRegionsIds', new FormControl(zoneRegionsIds));
    } else {
      this.configurationForm.patchValue({ zoneRegionsIds });
    }
    if (idx === this.currentStep) this.incrementStep();
  }

  public update(idx: number, value: any, noUpdate = false): void {
    if (!!value?.isTrusted) {
      return;
    }

    if (!noUpdate) {
      const controlName = this.selectedAlertFlow.steps[idx].formControlName;
      this.selectedAlertFlow.steps[idx].value = value;
      this.configurationForm.patchValue({
        [controlName]: value,
      });
      if (controlName === 'doors') {
        const cameras: KeyValuePairs<CameraLookup> = {};
        for (let door of value) {
          cameras[door.cameraId] = {
            cameraId: door.cameraId,
            edgeId: door.edgeId,
            locationId: door.locationId,
          };
        }
        this.configurationForm.patchValue({
          camera: Object.values(cameras),
        });
      } else if (
        controlName === 'metric' &&
        this.selectedFlowCategory === AlertCategory.Monitoring
      ) {
        this.updateMonitoringMetricDependentSteps(value as HealthMetric);
      } else if (controlName === 'shelves') {
        const cameraControl = this.configurationForm.get('camera');
        const selectedShelfIds = value;

        this.store$
          .pipe(
            select(ShelfsSelectors.selectCamerasByShelfIds(selectedShelfIds)),
            take(1),
          )
          .subscribe((cameras) => {
            cameraControl.setValue(cameras);
          });
      }
    }
    if (idx === this.currentStep) {
      this.incrementStep();
    }
  }

  public async updateShelves(idx: number, value: any): Promise<void> {
    if (!!value?.isTrusted) {
      return;
    }
    const cameraIds = await lastValueFrom(
      this.store$
        .select(ShelfsSelectors.selectCamerasByShelfIds(value))
        .pipe(take(1)),
    );
    const cameras = await lastValueFrom(
      this.store$
        .select(CameraSelectors.selectCameraLookupsByIds(cameraIds))
        .pipe(take(1)),
    );
    this.configurationForm.patchValue({
      shelves: value,
      camera: cameras,
    });
    if (idx === this.currentStep) {
      this.incrementStep();
    }
  }

  public updateVehicleSelection(
    idx: number,
    value: AlertsV2VehicleSelection,
  ): void {
    this.configurationForm.patchValue({
      vehicles: value.vehicles,
      vehicleGroups: value.vehicleGroups,
    });
    if (idx === this.currentStep) {
      this.incrementStep();
    }
  }

  private updateMonitoringMetricDependentSteps(metric: HealthMetric): void {
    const utilizationMetrics = [
      HealthMetric.CPU,
      HealthMetric.RAM,
      HealthMetric.DISK,
      HealthMetric.NETWORK_DROPS,
      HealthMetric.NETWORK_ERRORS,
    ];

    const unitStep = this.selectedAlertFlow.steps.find(
      (s) => s.formControlName === 'unit',
    );
    const valueStep = this.selectedAlertFlow.steps.find(
      (s) => s.formControlName === 'value',
    );

    let valueMin = 0;
    let valueMax = 200000;

    if (utilizationMetrics.includes(metric)) {
      valueMin = 0;
      valueMax = 100;
      if (unitStep) {
        unitStep.options = {
          options: HealthMetricUtilizationUnit,
          optionsStr: Object.values(EventModels.healthMetricUtilizationUnitStr),
          placeholder: 'ALERT_TEMPLATES.HEALTH_METRIC_UNIT.PERCENTAGE',
          numberControl: true,
          disable: true,
        };
        unitStep.value = HealthMetricUtilizationUnit.Percentage;
      }
    } else if (metric === HealthMetric.TEMPERATURE) {
      valueMin = -500;
      valueMax = 500;
      if (unitStep) {
        unitStep.options = {
          options: HealthMetricDegreeUnit,
          optionsStr: Object.values(EventModels.healthMetricDegreeUnitStr),
          placeholder: 'ALERT_TEMPLATES.HEALTH_METRIC_UNIT.CELSIUS',
          numberControl: true,
          disable: false,
        };
        unitStep.value = HealthMetricDegreeUnit.Celsius;
      }
    }

    if (valueStep) {
      valueStep.options = { min: valueMin, max: valueMax, step: 1 };
      const currentValue = this.configurationForm.get('value')?.value;
      if (currentValue != null && currentValue !== '') {
        const clamped = Math.min(
          Math.max(Number(currentValue), valueMin),
          valueMax,
        );
        valueStep.value = clamped;
        this.configurationForm.patchValue({ value: clamped });
      }
    }

    if (unitStep) {
      this.configurationForm.patchValue({ unit: unitStep.value });
    }
    this.changeDetectorRef.detectChanges();
  }

  public onPhysicalDoorSelected(door: any): void {
    this.selectedPhysicalDoor = door;
    // Reset camera selection when door changes
    const cameraControlName = 'camera';
    const cameraStepIdx = this.selectedAlertFlow?.steps?.findIndex(
      (s) => s.formControlName === cameraControlName,
    );
    if (cameraStepIdx >= 0) {
      this.configurationForm.patchValue({
        [cameraControlName]: null,
      });
      this.selectedAlertFlow.steps[cameraStepIdx].value = null;
    }
    this.changeDetectorRef.detectChanges();
  }

  public onObjectStateSelected(objectState: ObjectStateItem | null): void {
    if (objectState) {
      this.selectedObjectStateStates = objectState.states || [];
      this.configurationForm.patchValue({
        camera: [
          {
            cameraId: objectState.cameraId,
            edgeId: objectState.edgeId,
            locationId: objectState.locationId,
          },
        ],
      });
    } else {
      this.selectedObjectStateStates = [];
      this.configurationForm.patchValue({
        camera: [],
      });
    }
    // Reset state selections when object state changes (use empty arrays for multiselect)
    const fromStateIdsControl =
      this.selectedAlertFlow?.form?.get('fromStateIds');
    const toStateIdsControl = this.selectedAlertFlow?.form?.get('toStateIds');
    const stateIdsControl = this.selectedAlertFlow?.form?.get('stateIds');

    if (fromStateIdsControl) fromStateIdsControl.setValue([]);
    if (toStateIdsControl) toStateIdsControl.setValue([]);
    if (stateIdsControl) stateIdsControl.setValue([]);

    if (this.selectedAlertFlow?.steps) {
      for (const step of this.selectedAlertFlow.steps) {
        if (
          step.formControlName === 'fromStateIds' ||
          step.formControlName === 'toStateIds' ||
          step.formControlName === 'stateIds'
        ) {
          step.value = null;
        }
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  public getEventTags(): void {
    this.store$.dispatch(CustomEventsActions.getCustomEvents());
    this.selectAllCustomEvents$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customEvents: CustomEventModel.CustomEvent[]) => {
        this.eventTags = customEvents;
        this.eventTagIds = this.eventTags.map((eventTag) => eventTag._id);
        this.eventTagNames = this.eventTags.map((eventTag) => eventTag.name);

        for (let item of this.eventTags) {
          this.eventTagsKeyValuePairs[item._id] = item.name;
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  // ── Actions ("then do") — migrated from AlertV2CreateComponent ───────────────

  public newAction(): AlertV2Action {
    return {
      actionType: null,
      settingType: null,
    };
  }

  public addAction(): void {
    this.actions.push(this.newAction());
    this.setActions();
  }

  public deleteAction(i: number): void {
    this.actions.splice(i, 1);
    if (i === 0 && this.actions.length === 0) {
      this.addAction();
    }
    this.setActions();
  }

  public resetAction(index: number): void {
    delete this.actions[index].form;
    delete this.actions[index].steps;
    this.actions[index].disabled = true;
    this.setActions();
  }

  public loadActionFlow(index: number, value?: any, callData?: CallData): void {
    if (!callData) {
      return;
    }
    if (!callData?.type && !callData?.category && !callData?.selectName) {
      return;
    }
    if (!this.actions[index].callData) {
      this.actions[index].callData = callData;
    }
    this.actions[index].category = callData.category;
    let type: ActionType | SettingType;
    let flow: AlertV2Action;
    type = +callData.type;
    switch (callData.category) {
      case CallCategory.Action:
        this.actions[index].actionType = type as ActionType;
        flow = _.cloneDeep(
          this.AlertsV2Actions.filter((f) => f.actionType === type)[0],
        );
        if (type === ActionType.INTEGRATION_PROVIDER_ACTION) {
          flow.form.patchValue({ provider: callData.provider });
        }
        break;
      case CallCategory.Setting:
        this.actions[index].settingType = type as SettingType;
        flow = _.cloneDeep(
          this.AlertsV2SettingActions.filter((f) => f.settingType === type)[0],
        );
        break;
    }
    if (!!flow) {
      this.actions[index].steps = _.cloneDeep(flow.steps);
      this.actions[index].form = _.cloneDeep(flow.form);
      if (value) {
        if (type === ActionType.REMOTE_IO) {
          value = EventModels.normalizeRemoteIoActionFormValue(value);
        }
        if (type === ActionType.INTEGRATION_PROVIDER_ACTION) {
          this.actions[index].form.patchValue({
            ...value,
            provider: callData.provider,
          });
        } else {
          this.actions[index].form.patchValue({ ...value });
        }
        for (let step of this.actions[index].steps) {
          step.value = value[step.formControlName];
        }
      }
      this.actions[index].form.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.setActions();
        });
    } else {
      this.resetAction(index);
    }
    this.setActions();
    this.changeDetectorRef.detectChanges();
  }

  public setActions(): void {
    const editedActions: AlertV2SelectedAction[] = this.actions.map(
      (action) => {
        return {
          category: action.category,
          settingType: action.settingType,
          actionType: action.actionType,
          formValue: action.form?.value,
        };
      },
    );
    const actions = this.editConfiguration()
      ? this.mergeEditedActions(editedActions)
      : editedActions;
    this.store$.dispatch(AlertsV2StoreActions.setActions({ actions }));
    this.getDisableOptions();
  }

  private hydrateActions(actions: AlertV2SelectedAction[]): void {
    this.actions = [];
    this.editableActionSlots = [];
    for (const [index, action] of actions.entries()) {
      const callData = this.resolveCallData(action);
      if (!callData) continue;
      const draftIndex = this.actions.length;
      this.actions.push(this.newAction());
      this.editableActionSlots.push(index);
      this.loadActionFlow(draftIndex, action.formValue, callData);
    }
    this.setActions();
  }

  private resolveCallData(action: AlertV2SelectedAction): CallData | null {
    const category =
      action.settingType != null ? CallCategory.Setting : CallCategory.Action;
    const options = this.AlertV2CallGroup$.value
      .flatMap((group) => group.options ?? [])
      .map((option) => option?.value)
      .filter((value): value is CallData => !!value);
    const match = options.find((value) => {
      if (+value.category !== +category) return false;
      if (category === CallCategory.Setting)
        return +value.type === +action.settingType;
      if (+action.actionType === +ActionType.INTEGRATION_PROVIDER_ACTION) {
        return value.provider === action.formValue?.provider;
      }
      return +value.type === +action.actionType;
    });
    return match ? _.cloneDeep(match) : null;
  }

  private mergeEditedActions(
    editedActions: AlertV2SelectedAction[],
  ): AlertV2SelectedAction[] {
    const editableSlots = new Set(this.editableActionSlots);
    const merged: AlertV2SelectedAction[] = [];
    let editedIndex = 0;
    for (const [index, original] of this.originalActions.entries()) {
      if (!editableSlots.has(index)) {
        merged.push(_.cloneDeep(original));
      } else if (editedIndex < editedActions.length) {
        const edited = editedActions[editedIndex++];
        merged.push({
          ..._.cloneDeep(original),
          ...edited,
          formValue: {
            ..._.cloneDeep(original.formValue ?? {}),
            ...(edited.formValue ?? {}),
            ...(original.formValue?.['params'] &&
            typeof original.formValue['params'] === 'object'
              ? {
                  params: {
                    ..._.cloneDeep(original.formValue['params']),
                    ...(edited.formValue?.['params'] ?? {}),
                  },
                }
              : {}),
          },
        });
      }
    }
    return [...merged, ...editedActions.slice(editedIndex)];
  }

  public updateAction(action: AlertV2Action, idx: number, value: any): void {
    if (!!value?.isTrusted) {
      return;
    }
    const controlName = action.steps[idx].formControlName;
    action.steps[idx].value = value;
    action.form.patchValue({
      [controlName]: value,
    });
    if (idx <= action.currentStep) {
      action.currentStep++;
    }
    this.setActions();
  }

  public updateNotifyAction(
    action: AlertV2Action,
    idx: number,
    value: StepNotificationMsgDialogResponse,
  ): void {
    action.form.patchValue(value);
    if (idx <= action.currentStep) {
      action.currentStep++;
    }
  }

  public get isPanicEvent(): boolean {
    return this.selectedFlowCategory === AlertCategory.System && this.selectedFlowType === SystemType.Panic;
  }

  public get hasPriority(): boolean {
    const settings = this.actions.filter(
      (action) => +action?.callData?.category === CallCategory.Setting,
    );
    return !!settings.filter(
      (action) => +action.settingType === SettingType.PRIORITY,
    ).length;
  }

  public get hasReactivation(): boolean {
    const settings = this.actions.filter(
      (action) => +action?.callData?.category === CallCategory.Setting,
    );
    return !!settings.filter(
      (action) => +action.settingType === SettingType.REACTIVIATION_THREASHOLD,
    ).length;
  }

  public getDisableOptions(): void {
    if (this.hasPriority) {
      this.disableOptions.push({
        groupIdx: 1,
        optionCategory: CallCategory.Setting,
        optionType: SettingType.PRIORITY,
      });
    }
    if (this.hasReactivation) {
      this.disableOptions.push({
        groupIdx: 1,
        optionCategory: CallCategory.Setting,
        optionType: SettingType.REACTIVIATION_THREASHOLD,
      });
    }
  }

  public disableCoreActions(): void {
    this.disableOptions = [
      ...this.disableOptions,
      ...[
        {
          groupIdx: 0,
          optionCategory: CallCategory.Action,
          optionType: ActionType.GPIO,
        },
        {
          groupIdx: 0,
          optionCategory: CallCategory.Action,
          optionType: ActionType.SYSLOG,
        },
        {
          groupIdx: 0,
          optionCategory: CallCategory.Action,
          optionType: ActionType.SPEAKER,
        },
      ],
    ];
  }

  public async initIntegrations(): Promise<void> {
    const isDeveloper = await lastValueFrom(
      this.selectIsDeveloper$.pipe(take(1)),
    );
    // The initial store value is null until getIntegrations resolves — wait for it.
    const integrations = await lastValueFrom(
      this.selectIntegrations$.pipe(
        filter((i) => !!i),
        take(1),
      ),
    );
    const groups = structuredClone(AlertV2CallGroup);

    if (!isDeveloper) {
      groups[0].options = groups[0].options.filter(
        (option) => !option?.value?.developer,
      );
    }

    // Workflow actions are policy-only.
    groups[0].options = groups[0].options.filter(
      (option) => option?.value?.type !== ActionType.START_WORKFLOW,
    );

    for (let integration of integrations) {
      if (integrationActions[integration]) {
        groups[0].options.push(integrationActions[integration]);
      }
      if (
        integration === IntegrationsModel.Providers.NoonLight ||
        integration === IntegrationsModel.Providers.Lumana
      ) {
        groups[0].options.push(
          integrationActions[IntegrationsModel.Providers.Dispatch],
        );
      }
    }
    groups[0].options.push(integrationActions['webhook']);
    groups[0].options.push(integrationActions['microsoftTeams']);
    groups[0].options.push(integrationActions['variables']);

    this.AlertV2CallGroup$.next(groups);

    combineLatest([
      this.selectSlackChannels$.pipe(
        filter((ch) => ch !== null),
        take(1),
      ),
      this.selectTeamsChannels$.pipe(
        filter((ch) => ch !== null),
        take(1),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([slackChannels, teamsChannels]) => {
        let updatedGroups = structuredClone(groups);

        updatedGroups = this.applyConditions(updatedGroups, { slackChannels });
        updatedGroups = this.applyTeamsConditions(updatedGroups, {
          teamsChannels,
        });

        this.AlertV2CallGroup$.next(updatedGroups);
      });
  }

  private applyConditions(
    groups: StepOptionSelectorGroup[],
    data: {
      slackChannels: IntegrationsModel.SlackChannel[];
    },
  ): StepOptionSelectorGroup[] {
    const updatedGroups = structuredClone(groups);

    OPTION_CONDITIONS.forEach((condition) => {
      const option = updatedGroups[0]?.options?.find(
        (opt) => opt?.value?.provider === condition.provider,
      );

      if (option && condition.check(data.slackChannels)) {
        condition.update(option);
        if (option.tooltip) {
          option.tooltip = this.translateService.instant(option.tooltip);
        }
      }
    });

    return updatedGroups;
  }

  private applyTeamsConditions(
    groups: StepOptionSelectorGroup[],
    data: {
      teamsChannels: MicrosoftTeamsModels.ChannelModel[];
    },
  ): StepOptionSelectorGroup[] {
    const updatedGroups = structuredClone(groups);

    OPTION_CONDITIONS_TEAMS.forEach((condition) => {
      const option = updatedGroups[0]?.options?.find(
        (opt) => opt?.value?.provider === condition.provider,
      );

      if (option && condition.check(data.teamsChannels)) {
        condition.update(option);
        if (option.tooltip) {
          option.tooltip = this.translateService.instant(option.tooltip);
        }
      }
    });

    return updatedGroups;
  }

  // ── Settings & schedule — migrated from AlertV2CreateComponent ───────────────

  public initSettings(): void {
    this._settings = {
      priority: '' + initialAlertSettings.priority,
      confidence: '' + initialAlertSettings.confidence,
      reactivationTh: initialAlertSettings.reactivationTh,
      autoArchive: {
        enabled: initialAlertSettings.autoArchive.enabled,
        duration: initialAlertSettings.autoArchive.duration,
      },
      show: {
        disabled: false,
        on: [AlertsV2ShowSettings.ALERT_PAGE, AlertsV2ShowSettings.WALLS],
      },
      message: initialAlertSettings.message,
    };
  }

  public openSettings(): void {
    this.matDialog.open(AlertsV2SettingsDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '420px',
      maxHeight: '80vh',
      data: {
        variablesAutoComplete: this.selectVariablesAutoComplete$.getValue(),
      },
    });
  }

  public isDefaultSettings(settings: AlertsV2Settings): boolean {
    const initialWithMessage = _.cloneDeep(initialAlertSettings);
    initialWithMessage.message = this.alertMessage;
    return (
      _.isEqual(settings, baseAlertsV2Settings) ||
      _.isEqual(settings, initialAlertSettings) ||
      _.isEqual(settings, baseAlertsV2Settings2) ||
      _.isEqual(settings, initialWithMessage)
    );
  }

  public onScheduleStepChange(
    stepIndex: number,
    event: {
      scheduleId: string;
      schedule?: AlertV2Schedule[];
      isCustom?: boolean;
    },
  ): void {
    if (event.isCustom) {
      // For custom schedules, store the schedule array in the step value.
      this.update(stepIndex, event.schedule);
      // Update selectedFlow with empty scheduleId but DON'T clear the form control
      // (the picker needs to keep its CUSTOM_SCHEDULE_VALUE to show custom text).
      this.updateSelectedFlowWithScheduleId('');
    } else if (event.scheduleId) {
      this.settingsForm.patchValue({ scheduleId: event.scheduleId });
      this.update(stepIndex, event.schedule);
    } else {
      // For "all times" (null/empty)
      this.settingsForm.patchValue({ scheduleId: '' });
      this.update(stepIndex, null);
    }
  }

  public onCustomScheduleChange(
    stepIndex: number,
    customSchedule: AlertV2Schedule[],
  ): void {
    this.update(stepIndex, customSchedule);
    this.updateSelectedFlowWithScheduleId('');
  }

  private updateSelectedFlowWithScheduleId(scheduleId: string): void {
    this.store$
      .pipe(
        select((state) => state.alertsV2State.selectedFlow),
        take(1),
      )
      .subscribe((currentSelectedFlow) => {
        if (currentSelectedFlow) {
          const updatedSelectedFlow: AlertV2SelectedFlow = {
            ...currentSelectedFlow,
            scheduleId,
          };
          this.store$.dispatch(
            AlertsV2StoreActions.setSelectedFlow({
              selectedFlow: updatedSelectedFlow,
            }),
          );
        }
      });
  }

  public get scheduleIdControl(): FormControl {
    return this.settingsForm.get('scheduleId') as FormControl;
  }

  // ── Value getters used by the template ───────────────────────────────────────

  public get platesValue(): AlertsV2Plates | null {
    return this.configurationForm.get('plates')?.value ?? null;
  }

  public get vehiclesValue(): AlertsV2Vehicles {
    return (
      this.configurationForm.get('vehicles')?.value ?? {
        list: [],
        appears: this.platesValue?.appears ?? true,
        unrecognized: this.platesValue?.unrecognized ?? false,
      }
    );
  }

  public get vehicleGroupsValue(): VehicleGroupsModel.VehicleGroupItem[] {
    return this.configurationForm.get('vehicleGroups')?.value ?? [];
  }

  public getDirty(form: UntypedFormGroup): any {
    return this.sharedService.getDirtyValues(form);
  }
}
